import { useTranslations } from 'next-intl';
import Link from '@atoms/Link/Link';
import Heading from '@atoms/Heading/Heading';
import Text from '@atoms/Text/Text';
import ScreenReaderMessage from '@atoms/ScreenReaderMessage/ScreenReaderMessage';
import Table from '@atoms/Table/Table';
import {
  StyledHeadingTooltipContainer,
  StyledHtmlElement,
  StyledProductTableOfInformationSection,
} from './ProductTableOfInformation.styles';
import type { AxfoodProductDetailsViewModel, NutrientFactData } from '@occ/api-client';
import Info from '@icons/info.svg';
import MultipackTableOfInformation from '@molecules/ProductTableOfInformation/MultipackTableOfInformation/MultipackTableOfInformation';
import DailyRecommendedIntakeTable from '@molecules/ProductTableOfInformation/DailyRecommendedIntakeTable/DailyRecommendedIntakeTable';
import Br from '@atoms/Br/Br';
import IconTooltip from '../IconTooltip/IconTooltip';

interface Props {
  data: AxfoodProductDetailsViewModel;
}

const ProductTableOfInformation = ({ data }: Props) => {
  const t = useTranslations('product');
  const tTooltip = useTranslations('tooltip');
  const tCommon = useTranslations('common');

  return (
    <div>
      {data.activeSubstance && (
        <StyledProductTableOfInformationSection data-testid="activeSubstance">
          <Heading variant="h3" label>
            {t('activeSubstance')}
          </Heading>
          <Text type="body" color="black">
            {data.activeSubstance}
          </Text>
        </StyledProductTableOfInformationSection>
      )}

      {data.medicineType && (
        <StyledProductTableOfInformationSection data-testid="medicineType">
          <Heading variant="h3" label>
            {t('medicineType')}
          </Heading>
          <Text type="body" color="black">
            {data.medicineType}
          </Text>
        </StyledProductTableOfInformationSection>
      )}

      {data.medicineLink && (
        <StyledProductTableOfInformationSection data-testid="medicineLink">
          <Text type="body" color="black">
            <span>{`${t('medicineLeafletStart')} `}</span>
            <Link href={data.medicineLink} target="_blank">
              {t('medicineLeafletLink')}
            </Link>
            <span>{` ${t('medicineLeafletEnd')}`}</span>
          </Text>
        </StyledProductTableOfInformationSection>
      )}

      {data.description && (
        <StyledProductTableOfInformationSection data-testid="description">
          <Text type="body" color="black">
            {data.description}
          </Text>
        </StyledProductTableOfInformationSection>
      )}

      {data.extraInfoCode === 'FFM' && (
        <StyledProductTableOfInformationSection data-testid="extraInfoCode">
          <Text type="body" color="black">
            {t('extraInfoCode')}
          </Text>
        </StyledProductTableOfInformationSection>
      )}

      {data.medicineType && data.ean && (
        <StyledProductTableOfInformationSection data-testid="ean">
          <Heading variant="h3" label>
            {t('gtin')}
          </Heading>
          <Text type="body" color="black">
            {data.ean}
          </Text>
        </StyledProductTableOfInformationSection>
      )}

      {data.categoryGroupText && (
        <StyledProductTableOfInformationSection data-testid="categoryGroupText">
          <StyledHtmlElement dangerouslySetInnerHTML={{ __html: data.categoryGroupText }} />
        </StyledProductTableOfInformationSection>
      )}

      {data?.otherCountries && data.otherCountries.length > 0 && (
        <StyledProductTableOfInformationSection data-testid="otherCountries">
          <Heading variant="h3" label>
            {t('otherCountries')}
          </Heading>
          <Text type="body" color="black">
            {data.otherCountries.join(', ')}
          </Text>
          <Br />
          <Text type="body" color="black">
            {t('otherCountriesInfo')}
          </Text>
        </StyledProductTableOfInformationSection>
      )}

      {(data.provinceStatement || data.countryOfOriginStatement) && (
        <StyledProductTableOfInformationSection>
          <Heading variant="h3" label>
            {t('origin')}
          </Heading>
          {data.provinceStatement ? (
            <Text type="body" color="black" data-testid="provinceStatement">
              {data.provinceStatement}
            </Text>
          ) : (
            <Text type="body" color="black" data-testid="countryOfOriginStatement">
              {data.countryOfOriginStatement}
            </Text>
          )}
        </StyledProductTableOfInformationSection>
      )}

      {data.tradeItemCountryOfOrigin && (
        <StyledProductTableOfInformationSection data-testid="tradeItemCountryOfOrigin">
          <StyledHeadingTooltipContainer>
            <Heading variant="h3" label>
              {t('countryOfOrigin')}
              <ScreenReaderMessage>{tTooltip('tooltip.originCountry')}</ScreenReaderMessage>
            </Heading>
            <IconTooltip
              content={tTooltip('tooltip.originCountry')}
              iconProps={{ svg: Info, size: 16, 'aria-label': tCommon('aria.infoIcon') }}
              align="center"
              position="bottom"
              color="black"
              isPortal
            />
          </StyledHeadingTooltipContainer>
          <Text type="body" color="black">
            {data.tradeItemCountryOfOrigin}
          </Text>
        </StyledProductTableOfInformationSection>
      )}

      {data.nutritionsFactList?.length > 0 && data.nutrientHeaders?.length <= 0 && (
        <StyledProductTableOfInformationSection data-testid="nutritionsFactList">
          <Table>
            <thead>
              <tr>
                <td>
                  <Heading variant="h3" label>
                    {t('nutrition.title')}
                  </Heading>
                </td>
                <td>
                  {data.nutritionDescription && (
                    <Text type="body" size="small" color="darkerGray">
                      {t('nutrition.portion', { portion: data.nutritionDescription })}
                    </Text>
                  )}
                </td>
              </tr>
            </thead>
            <tbody>
              {data.nutritionsFactList?.map((nutrition: NutrientFactData, i: number) => (
                <tr key={`${nutrition.typeCode}-${nutrition.unitCode}-${i}`}>
                  <td>{nutrition?.typeCode}</td>
                  <td>
                    {nutrition?.measurementPrecisionCode} {nutrition?.value} {nutrition?.unitCode}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </StyledProductTableOfInformationSection>
      )}

      {data.nutrientComponents?.length > 0 && (
        <MultipackTableOfInformation nutrientComponents={data.nutrientComponents} />
      )}

      {data.nutrientHeaders?.length > 0 && <DailyRecommendedIntakeTable data={data.nutrientHeaders} />}

      {(data.nutrientHeaders?.length > 0 ||
        data.nutritionsFactList?.length > 0 ||
        data.nutrientComponents?.length > 0) && (
        <StyledProductTableOfInformationSection>
          <Text type="body" color="black" size="small">
            {t('disclaimer')}
          </Text>
        </StyledProductTableOfInformationSection>
      )}
    </div>
  );
};

export default ProductTableOfInformation;
